import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from './UserContext';
import '../styles/studentAnalytics.css'; // Import the CSS file

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  Cell
} from 'recharts';
import { 
  format as formatDate,
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  eachWeekOfInterval, 
  eachMonthOfInterval, 
  startOfDay, 
  endOfDay,
  differenceInDays
} from 'date-fns';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  StyledEngineProvider,
  TextField
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = 'https://gnrl-bknd-server.onrender.com/api';

// Create a theme specifically for the date picker
const datePickerTheme = createTheme({
  components: {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          '&.Mui-selected': {
            backgroundColor: '#e0e0e0',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
  },
});

const datePickerStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#bdbdbd',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2196f3',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#fff',
    fontSize: '0.9rem',
    '&.Mui-focused': {
      color: '#fff',
    }
  },
  '& .MuiOutlinedInput-input': {
    color: '#fff',
    fontSize: '0.95rem',
  },
  '& .MuiSvgIcon-root': {
    color: '#fff',
    fontSize: '1.3rem',
  }
};

const Analytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState('daily');
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null);
  const {user} = useContext(UserContext);
  const [analyticsData, setAnalyticsData] = useState({
    dailyLogins: [],
    completedFilesByFolder: [],
    completedFilesCount: 0,
    completedFilesByDate: [],
    practiceTimeByFolder: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folderStructure, setFolderStructure] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [fileRepeats, setFileRepeats] = useState([]);
  const [isLoadingFileRepeats, setIsLoadingFileRepeats] = useState(false);
  const [startMonth, setStartMonth] = useState('');
  const [startDay, setStartDay] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endYear, setEndYear] = useState('');

  // Custom colors for the bar chart
  const FOLDER_COLORS = {
    total: '#EF8354',  // Orange color for total files
    completed: '#4F86C6'  // Blue color for completed files
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    const today = new Date();
    
    switch (newRange) {
      case 'daily':
        setStartDate(subDays(today, 7));
        setEndDate(today);
        break;
      case 'weekly':
        setStartDate(subDays(today, 30));
        setEndDate(today);
        break;
      case 'monthly':
        setStartDate(subDays(today, 90));
        setEndDate(today);
        break;
      default:
        break;
    }
  };

  const getTickInterval = (data) => {
    if (!data || data.length === 0) return 0;
    const dataLength = data.length;
    
    // Adjust interval based on data length
    if (dataLength <= 7) return 0;        // Show all points for a week or less
    if (dataLength <= 14) return 1;       // Show every other day for two weeks
    if (dataLength <= 31) return 2;       // Show every third day for a month
    if (dataLength <= 90) return 6;       // Show weekly for three months
    return Math.floor(dataLength / 12);   // Show roughly 12 points for longer periods
  };

  const formatXAxisDate = (dateStr, dateRange) => {
    const date = new Date(dateStr);
    switch (dateRange) {
      case 'daily':
        return formatDate(date, 'MMM d');
      case 'weekly':
        return formatDate(date, 'MMM d');
      case 'monthly':
        return formatDate(date, 'MMM yyyy');
      default:
        return dateStr;
    }
  };

  const formatYAxisTick = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value;
  };

  const processDataByDateRange = (data, start, end, rangeType) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    // Ensure endDate includes the entire current day
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);
    
    const daysDifference = differenceInDays(adjustedEndDate, startDate);

    let intervals = [];
    let dateFormat = 'MMM d';

    switch (rangeType) {
      case 'daily':
        intervals = eachDayOfInterval({ start: startDate, end: adjustedEndDate });
        dateFormat = daysDifference > 30 ? 'MMM d' : 'MMM d';
        break;
      case 'weekly':
        intervals = eachWeekOfInterval({ start: startDate, end: adjustedEndDate });
        dateFormat = daysDifference > 90 ? 'MMM d' : "'Week of' MMM d";
        break;
      case 'monthly':
        intervals = eachMonthOfInterval({ start: startDate, end: adjustedEndDate });
        dateFormat = 'MMM yyyy';
        break;
      default:
        intervals = eachDayOfInterval({ start: startDate, end: adjustedEndDate });
    }

    const processedData = intervals.map(date => ({
      date: formatDate(date, dateFormat),
      count: 0,
      tooltipDate: formatDate(date, 'MMM d, yyyy')
    }));

    data.forEach(item => {
      try {
        const itemDate = new Date(item.date);
        let intervalIndex = -1;

        switch (rangeType) {
          case 'daily':
            intervalIndex = intervals.findIndex(date => 
              startOfDay(date).getTime() === startOfDay(itemDate).getTime()
            );
            break;
          case 'weekly':
            intervalIndex = intervals.findIndex(date =>
              itemDate >= startOfWeek(date) && itemDate < endOfWeek(date)
            );
            break;
          case 'monthly':
            intervalIndex = intervals.findIndex(date =>
              itemDate >= startOfMonth(date) && itemDate < endOfMonth(date)
            );
            break;
        }

        if (intervalIndex !== -1) {
          processedData[intervalIndex].count += item.count;
        }
      } catch (err) {
        console.error('Error processing data item:', item, err);
      }
    });

    return processedData;
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Set end date to include the full day
      const endOfCurrentDay = new Date(endDate);
      endOfCurrentDay.setHours(23, 59, 59, 999);
      
      // Format dates properly for API calls
      const formattedStartDate = formatDate(new Date(startDate), 'yyyy-MM-dd');
      const formattedEndDate = formatDate(endOfCurrentDay, 'yyyy-MM-dd');
      const userParam = user ? `&username=${user}` : '';
      
      console.log('Fetching analytics with date range:', { 
        startDate: formattedStartDate, 
        endDate: formattedEndDate,
        user: user || 'all' 
      });
      
      // First fetch all users to ensure we have data for every user
      const usersResponse = await axios.get(`${API_BASE_URL}/users`);
      
      const [dailyLoginsRes, completedFilesByFolderRes, completedFilesCountRes, completedFilesByDateRes, practiceTimeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/analytics/daily-logins?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/completed-files-by-folder?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/completed-files-count?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/completed-files-by-date?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`),
        axios.get(`${API_BASE_URL}/analytics/practice-time-by-folder?startDate=${formattedStartDate}&endDate=${formattedEndDate}${userParam}`)
      ]);

      const processedDailyLogins = processDataByDateRange(
        dailyLoginsRes.data,
        startDate,
        endOfCurrentDay,
        dateRange
      );

      // Process the folder data to create shorter display names
      const processedFolderData = completedFilesByFolderRes.data.map(folder => {
        // Get just the folder name without full path for display
        const displayName = folder.name.split('/').pop() || folder.name;
        return {
          ...folder,
          displayName
        };
      });

      setAnalyticsData({
        dailyLogins: processedDailyLogins,
        completedFilesByFolder: processedFolderData,
        completedFilesCount: completedFilesCountRes.data.count,
        completedFilesByDate: completedFilesByDateRes.data,
        practiceTimeByFolder: practiceTimeRes.data
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to fetch analytics data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [startDate, endDate, selectedUser, dateRange]);

  // Add to useEffect for initial load
  useEffect(() => {
    const fetchFolderStructure = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/analytics/folder-structure`);
        console.log('Folder structure:', response.data);
        setFolderStructure(response.data);
      } catch (error) {
        console.error('Error fetching folder structure:', error);
        setError('Failed to fetch folder structure');
      }
    };

    fetchFolderStructure();
  }, []);

  // Add function to fetch file repeats for selected folder
  const fetchFileRepeats = async (folderPath) => {
    try {
      setIsLoadingFileRepeats(true);
      console.log('Fetching file repeats for folder:', folderPath);
      
      const response = await axios.get(`${API_BASE_URL}/analytics/file-repeats`, {
        params: {
          startDate: formatDate(startDate, 'yyyy-MM-dd'),
          endDate: formatDate(endDate, 'yyyy-MM-dd'),
          username: user,
          folderPath
        }
      });
      
      console.log('File repeats response:', response.data);
      setFileRepeats(response.data);
    } catch (error) {
      console.error('Error fetching file repeats:', error);
      setError('Failed to fetch file repeats data');
    } finally {
      setIsLoadingFileRepeats(false);
    }
  };

  // Add handler for folder selection
  const handleFolderSelect = (event) => {
    const folderPath = event.target.value;
    console.log('Selected folder:', folderPath);
    setSelectedFolder(folderPath);
    fetchFileRepeats(folderPath);
  };

  // Recursive function to render folder options
  const renderFolderOptions = (folder, level = 0) => {
    const indent = '  '.repeat(level);
    return [
      <MenuItem key={folder.path} value={folder.path}>
        {indent + folder.name}
      </MenuItem>,
      ...(folder.subfolders || []).flatMap(sf => renderFolderOptions(sf, level + 1))
    ];
  };

  // Render the total completed files card
  const renderTotalFilesCard = () => (
    <Card sx={{ 
      borderRadius: '12px', 
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      backgroundColor: '#fff',
      mb: 3,
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 16px rgba(0,0,0,0.12)'
      }
    }}>
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        py: 3
      }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
          Total Completed Audio Files
        </Typography>
        
        <Typography variant="h2" sx={{ 
          fontWeight: 700, 
          color: '#5e35b1',
          mt: 1,
          display: 'flex',
          alignItems: 'center'
        }}>
          {analyticsData.completedFilesCount}
          <Typography variant="body1" component="span" sx={{ ml: 1, color: '#666', fontSize: '1rem' }}>
            files
          </Typography>
        </Typography>
        
        <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
          {formatDate(startDate, 'MMM d, yyyy')} - {formatDate(endDate, 'MMM d, yyyy')}
        </Typography>
      </CardContent>
    </Card>
  );

  // Render the engagement chart
  const renderEngagementChart = () => (
    <Card sx={{ 
      borderRadius: '12px', 
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      backgroundColor: '#fff',
      mb: 3
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
          Student Activity Over Time
        </Typography>
        {analyticsData.dailyLogins.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart 
              data={analyticsData.dailyLogins}
              margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
              <XAxis 
                dataKey="date"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={getTickInterval(analyticsData.dailyLogins)}
                tick={{ fontSize: 11, fill: '#666666' }}
                tickFormatter={(value) => formatXAxisDate(value, dateRange)}
                padding={{ left: 10, right: 10 }}
                tickMargin={15}
              />
              <YAxis 
                tickFormatter={(value) => {
                  if (value >= 1000) return `${(value/1000).toFixed(1)}k`;
                  return value;
                }}
                tick={{ fontSize: 11, fill: '#666666' }}
                width={40}
                tickMargin={5}
              />
              <Tooltip 
                formatter={(value) => [`${value} logins`, 'Logins']}
                labelFormatter={(label) => {
                  const dataPoint = analyticsData.dailyLogins.find(d => d.date === label);
                  return dataPoint ? dataPoint.tooltipDate : label;
                }}
                contentStyle={{ 
                  fontSize: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#5e35b1"
                fill="url(#colorGradient)"
                name="Logins"
                strokeWidth={2}
              />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#673ab7" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#9575cd" stopOpacity={0.2}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
            No activity data available for the selected date range
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Render the files by folder chart
  const renderFilesByFolderChart = () => (
    <Card sx={{ 
      borderRadius: '12px', 
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      backgroundColor: '#fff',
      mb: 3
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
          Audio Files by Folder
        </Typography>
        {analyticsData.completedFilesByFolder && analyticsData.completedFilesByFolder.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 450 : 350}>
            <BarChart 
              data={analyticsData.completedFilesByFolder}
              margin={isMobile ? 
                { top: 10, right: 10, left: 0, bottom: 150 } : 
                { top: 10, right: 10, left: 0, bottom: 80 }
              }
              layout="horizontal"
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
              <XAxis 
                dataKey="displayName"
                angle={isMobile ? -60 : -45}
                textAnchor="end"
                height={isMobile ? 140 : 80}
                interval={0}
                tick={{ fontSize: isMobile ? 9 : 10, fill: '#666666' }}
                tickMargin={isMobile ? 15 : 25}
              />
              <YAxis 
                type="number"
                tickFormatter={formatYAxisTick}
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#666666' }}
                width={isMobile ? 35 : 40}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'total') return [`${value} files`, 'Total Files'];
                  if (name === 'completed') return [`${value} files`, 'Completed Files'];
                  return [value, name];
                }}
                labelFormatter={(value) => {
                  const folder = analyticsData.completedFilesByFolder.find(f => f.displayName === value);
                  return folder ? `Folder: ${folder.name}` : value;
                }}
                contentStyle={{ 
                  fontSize: isMobile ? '11px' : '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  maxWidth: isMobile ? '200px' : '300px'
                }}
              />
              <Legend wrapperStyle={{ paddingTop: 10, fontSize: isMobile ? '11px' : '12px' }} />
              <Bar 
                dataKey="total" 
                name="Total Files" 
                fill={FOLDER_COLORS.total} 
                radius={[0, 0, 0, 0]}
              />
              <Bar 
                dataKey="completed" 
                name="Completed Files" 
                fill={FOLDER_COLORS.completed}
                radius={[0, 0, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
            No audio files found for the selected date range
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Render the files by date chart
  const renderFilesByDateChart = () => (
    <Card sx={{ 
      borderRadius: '12px', 
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      backgroundColor: '#fff',
      mb: 3
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
          Completed Files by Date
        </Typography>
        {analyticsData.completedFilesByDate && analyticsData.completedFilesByDate.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={analyticsData.completedFilesByDate}
              margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
              <XAxis 
                dataKey="date"
                angle={-45}
                textAnchor="end"
                height={70}
                interval={0}
                tick={{ fontSize: 10, fill: '#666666' }}
                tickMargin={25}
              />
              <YAxis 
                tickFormatter={formatYAxisTick}
                tick={{ fontSize: 11, fill: '#666666' }}
                width={40}
              />
              <Tooltip 
                formatter={(value) => [`${value} files`]}
                contentStyle={{ 
                  fontSize: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Bar 
                dataKey="count" 
                name="Completed Files" 
                fill="#4F86C6" 
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
            No completed files found for the selected date range
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Render the file repeats by folder chart
  const renderFileRepeatsByFolderChart = () => (
    <Card sx={{ 
      borderRadius: '12px', 
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      backgroundColor: '#fff',
      mb: 3
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
          File Repeats by Folder
        </Typography>
        {analyticsData.practiceTimeByFolder && analyticsData.practiceTimeByFolder.length > 0 ? (
          <Box>
            <ResponsiveContainer width="100%" height={isMobile ? 600 : 400}>
              <BarChart 
                data={analyticsData.practiceTimeByFolder}
                margin={isMobile ? 
                  { top: 20, right: 10, left: 10, bottom: 20 } : 
                  { top: 20, right: 30, left: 150, bottom: 20 }
                }
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.6} horizontal={true} vertical={false} />
                <XAxis 
                  type="number"
                  tickFormatter={(value) => `${value} times`}
                  tick={{ 
                    fontSize: isMobile ? 10 : 12,
                    fill: '#666666'
                  }}
                />
                <YAxis 
                  dataKey="name"
                  type="category"
                  width={isMobile ? 100 : 140}
                  tick={{ 
                    fontSize: isMobile ? 10 : 12,
                    fill: '#666666' 
                  }}
                  tickFormatter={isMobile ? (value) => value.split('/').pop() : undefined}
                  tickMargin={isMobile ? 5 : 10}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const folder = analyticsData.practiceTimeByFolder.find(f => f.name === label);
                      return (
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '8px',
                          fontSize: isMobile ? '11px' : '12px',
                          maxWidth: isMobile ? '200px' : '300px'
                        }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                            {isMobile ? folder.name.split('/').pop() : folder.name}
                          </p>
                          <p style={{ margin: '0 0 5px 0' }}>
                            {`Total Repeats: ${folder.totalRepeats}`}
                          </p>
                          <div style={{ 
                            maxHeight: isMobile ? '150px' : '200px', 
                            overflowY: 'auto',
                            fontSize: isMobile ? '10px' : '12px',
                            paddingRight: '8px'
                          }}>
                            <p style={{ margin: '5px 0', fontWeight: 'bold' }}>Files:</p>
                            {folder.files.map((file, index) => (
                              <p key={index} style={{ margin: '2px 0' }}>
                                {`${file.name}: ${file.repeats} times`}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="totalRepeats" 
                  name="Total Repeats" 
                  fill="#8884d8"
                  radius={[0, 4, 4, 0]}
                >
                  {analyticsData.practiceTimeByFolder.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`}
                      fill={`hsl(${(index * 25) % 360}, 70%, 60%)`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
            No repeat data found for the selected date range
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Add validation function
  const validateAndSetDate = (type, month, day, year) => {
    if (!month || !day || !year) return;

    const monthNum = parseInt(month);
    const dayNum = parseInt(day);
    const yearNum = parseInt(year);

    // Basic validation
    if (monthNum < 1 || monthNum > 12) return;
    if (dayNum < 1 || dayNum > 31) return;
    if (yearNum < 2000 || yearNum > 2100) return;

    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (type === 'start') {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  // Add handlers for input changes
  const handleStartDateChange = (field, value) => {
    switch (field) {
      case 'month':
        setStartMonth(value);
        validateAndSetDate('start', value, startDay, startYear);
        break;
      case 'day':
        setStartDay(value);
        validateAndSetDate('start', startMonth, value, startYear);
        break;
      case 'year':
        setStartYear(value);
        validateAndSetDate('start', startMonth, startDay, value);
        break;
    }
  };

  const handleEndDateChange = (field, value) => {
    switch (field) {
      case 'month':
        setEndMonth(value);
        validateAndSetDate('end', value, endDay, endYear);
        break;
      case 'day':
        setEndDay(value);
        validateAndSetDate('end', endMonth, value, endYear);
        break;
      case 'year':
        setEndYear(value);
        validateAndSetDate('end', endMonth, endDay, value);
        break;
    }
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={datePickerTheme}>
        <Box sx={{ 
          p: { xs: 2, md: 3 },
          ml: { xs: 0, md: '280px' }, // Match sidebar width
          maxWidth: '100%',
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
          boxSizing: 'border-box'
        }}>
          <Typography variant="h5" gutterBottom sx={{ 
            fontWeight: 600, 
            color: '#1a237e',
            mb: 2,
            pt: { xs: 5, md: 1 } // Add padding-top on mobile for the sidebar toggle button
          }}>
            My Analytics Dashboard
          </Typography>

          <Card sx={{ 
            mb: 3, 
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="date-range-label">Time Period</InputLabel>
                    <Select
                      labelId="date-range-label"
                      value={dateRange}
                      label="Time Period"
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                    >
                      <MenuItem value="daily">Last 7 days</MenuItem>
                      <MenuItem value="weekly">Last 30 days</MenuItem>
                      <MenuItem value="monthly">Last 90 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Start Date
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        label="MM"
                        value={startMonth}
                        onChange={(e) => handleStartDateChange('month', e.target.value)}
                        inputProps={{ maxLength: 2, placeholder: "MM" }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        label="DD"
                        value={startDay}
                        onChange={(e) => handleStartDateChange('day', e.target.value)}
                        inputProps={{ maxLength: 2, placeholder: "DD" }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        label="YYYY"
                        value={startYear}
                        onChange={(e) => handleStartDateChange('year', e.target.value)}
                        inputProps={{ maxLength: 4, placeholder: "YYYY" }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    End Date
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        label="MM"
                        value={endMonth}
                        onChange={(e) => handleEndDateChange('month', e.target.value)}
                        inputProps={{ maxLength: 2, placeholder: "MM" }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        label="DD"
                        value={endDay}
                        onChange={(e) => handleEndDateChange('day', e.target.value)}
                        inputProps={{ maxLength: 2, placeholder: "DD" }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        label="YYYY"
                        value={endYear}
                        onChange={(e) => handleEndDateChange('year', e.target.value)}
                        inputProps={{ maxLength: 4, placeholder: "YYYY" }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
              <CircularProgress size={50} thickness={4} sx={{ color: '#1a237e' }} />
            </Box>
          ) : (
            <Box>
              {renderTotalFilesCard()}
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  width: '100%', 
                  mb: 2,
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <Box 
                    onClick={() => setActiveTab(0)} 
                    sx={{ 
                      flex: 1, 
                      py: 2, 
                      textAlign: 'center', 
                      bgcolor: activeTab === 0 ? '#1976d2' : '#2c387e', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        bgcolor: activeTab === 0 ? '#1976d2' : '#3f51b5'
                      }
                    }}
                  >
                    Activity
                  </Box>
                  <Box 
                    onClick={() => setActiveTab(1)} 
                    sx={{ 
                      flex: 1, 
                      py: 2, 
                      textAlign: 'center', 
                      bgcolor: activeTab === 1 ? '#1976d2' : '#2c387e', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        bgcolor: activeTab === 1 ? '#1976d2' : '#3f51b5'
                      }
                    }}
                  >
                    Files by Folder
                  </Box>
                  <Box 
                    onClick={() => setActiveTab(2)} 
                    sx={{ 
                      flex: 1, 
                      py: 2, 
                      textAlign: 'center', 
                      bgcolor: activeTab === 2 ? '#1976d2' : '#2c387e', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        bgcolor: activeTab === 2 ? '#1976d2' : '#3f51b5'
                      }
                    }}
                  >
                    Files by Date
                  </Box>
                  <Box 
                    onClick={() => setActiveTab(3)} 
                    sx={{ 
                      flex: 1, 
                      py: 2, 
                      textAlign: 'center', 
                      bgcolor: activeTab === 3 ? '#1976d2' : '#2c387e', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        bgcolor: activeTab === 3 ? '#1976d2' : '#3f51b5'
                      }
                    }}
                  >
                    Repeats by Folder
                  </Box>
                </Box>
                
                {activeTab === 0 && renderEngagementChart()}
                {activeTab === 1 && renderFilesByFolderChart()}
                {activeTab === 2 && renderFilesByDateChart()}
                {activeTab === 3 && renderFileRepeatsByFolderChart()}
              </Box>
            </Box>
          )}
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default Analytics;
